import { Style, InputType, Preconditions, DynamicUpdateAction, DynamicUpdateCondition } from 'common/questionnaires/types';
import helpText from 'common/questionnaires/helpText';
import moment from 'moment';

const w8benQuestions = [
  {
    id: 'W8BEN.NAME.1',
    name: 'name',
    info: helpText.name,
    questionText: 'preQualifyingQuestionnaire.W8BEN.NAME.1.questionText',
    style: Style.Card,
    group: 'Name',
    answer: 'CUSTOMER-NAME',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Full Name', required: true }
      ]
    },
    errorMessage: 'First and last name are required.'
  },
  {
    id: 'W8BEN.NAME.2',
    name: 'businessname',
    info: helpText.disregardedEntity,
    questionText: 'preQualifyingQuestionnaire.W8BEN.NAME.2.questionText',
    style: Style.Embedded,
    group: 'Name',
    answer: 'DRE-NAME',
    preconditions: [Preconditions.CustomerRecordNameIsDRE],
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Disregarded Entity Name', required: false }
      ]
    }
  },
  {
    id: 'W8BEN.CITIZENSHIP.1',
    name: 'citizenship',
    info: helpText.countryOfCitizenship,
    questionText: 'preQualifyingQuestionnaire.W8BEN.CITIZENSHIP.1.questionText',
    style: Style.Card,
    group: 'Citizenship',
    inputs: {
      type: InputType.CountryDropdown,
      invalidSelection: 'Please Select Country',
      defaultSelection: 'Please Select Country'
    },
    errorMessage: 'Please provide a country of citizenship.'
  },
  {
    id: 'W8BEN.ADDRESS.1',
    name: 'residencyAddressLine1',
    info: helpText.residenceAddress,
    questionText: 'preQualifyingQuestionnaire.W8BEN.ADDRESS.1.questionText',
    style: Style.Card,
    group: 'Permanent Residence Address',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Street Address', required: true }
      ]
    },
    errorMessage: 'Please provide a street address.'
  },
  {
    id: 'W8BEN.ADDRESS.2',
    name: 'residencyCitystateprovince',
    style: Style.Embedded,
    group: 'Permanent Residence Address',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'City/State/Province/Territory', required: true }
      ]
    },
    errorMessage: 'Please provide a city, state, province, or territory.'
  },
  {
    id: 'W8BEN.ADDRESS.3',
    name: 'residencyCountry',
    style: Style.Embedded,
    group: 'Permanent Residence Address',
    inputs: {
      type: InputType.CountryDropdown,
      invalidSelection: 'Please Select Country',
      defaultSelection: 'Please Select Country'
    },
    errorMessage: 'Please provide a country.'
  },
  {
    id: 'W8BEN.ADDRESS.4',
    name: 'residencyPostalcode',
    style: Style.Embedded,
    group: 'Permanent Residence Address',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Postal Code' }
      ]
    }
  },
  {
    id: 'W8BEN.MAILADDRESS.1',
    name: 'mailAddressLine1',
    info: helpText.mailingAddress,
    questionText: 'preQualifyingQuestionnaire.W8BEN.MAILADDRESS.1.questionText',
    style: Style.Card,
    group: 'Mailing Address',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Street Address', required: false }
      ]
    },
    partialFieldConstraint: ['W8BEN.MAILADDRESS.2', 'W8BEN.MAILADDRESS.3'],
    errorMessage: 'Please provide a street address.'
  },
  {
    id: 'W8BEN.MAILADDRESS.2',
    name: 'mailCityStateProvince',
    style: Style.Embedded,
    group: 'Mailing Address',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'City/State/Province/Territory', required: false }
      ]
    },
    partialFieldConstraint: ['W8BEN.MAILADDRESS.1', 'W8BEN.MAILADDRESS.3'],
    errorMessage: 'Please provide a city, state, province, or territory.'
  },
  {
    id: 'W8BEN.MAILADDRESS.3',
    name: 'mailCountry',
    style: Style.Embedded,
    group: 'Mailing Address',
    inputs: {
      type: InputType.CountryDropdown,
      defaultValue: { value: '0', label: 'Please Select Country' },
    },
    partialFieldConstraint: ['W8BEN.MAILADDRESS.1', 'W8BEN.MAILADDRESS.2'],
    errorMessage: 'Please provide a country.'
  },
  {
    id: 'W8BEN.MAILADDRESS.4',
    name: 'mailPostalCode',
    style: Style.Embedded,
    group: 'Mailing Address',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Postal Code', required: false }
      ]
    },
  },
  {
    id: 'W8BEN.TIN.1',
    name: 'ftin',
    info: helpText.foreignTin,
    style: Style.Card,
    questionText: 'preQualifyingQuestionnaire.W8BEN.TIN.1.questionText',
    group: 'Tax Identification Number (TIN)',
    dynamicUpdate: {
      inputQuestionId: 'W8BEN.TIN.2',
      condition: DynamicUpdateCondition.Is,
      expectedValue: 0,
      action: DynamicUpdateAction.DisableAndClear,
      conditionMet: false
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Foreign TIN', required: false, validation: 'ftin' }
      ]
    }
  },
  {
    id: 'W8BEN.TIN.2',
    name: 'notlegallyrequired',
    questionText: 'preQualifyingQuestionnaire.W8BEN.TIN.2.questionText',
    style: Style.Collapsible,
    collapsibleHeaderText: 'More TIN Options',
    group: 'Tax Identification Number (TIN)',
    answer: 1,
    isAnswered: true,
    inputs: {
      type: InputType.YesNo,
      buttonTitles: [
        'Yes', 'No'
      ]
    }
  },
  {
    id: 'W8BEN.TIN.3',
    name: 'tin',
    info: helpText.w8ben.usTin,
    questionText: 'preQualifyingQuestionnaire.W8BEN.TIN.3.questionText',
    style: Style.Embedded,
    group: 'Tax Identification Number (TIN)',
    inputs: {
      type: InputType.TinField,
      fields: [
        { placeholder: 'REPLACE WITH TIN INPUT', required: false }
      ]
    }
  },
  {
    id: 'W8BEN.DOB.1',
    name: 'dob',
    questionText: 'preQualifyingQuestionnaire.W8BEN.DOB.1.questionText',
    style: Style.Card,
    group: 'Date of Birth',
    inputs: {
      type: InputType.DatePicker,
      maxDate: moment().subtract(1, 'days').toDate(),
      fields: [
        { placeholder: 'Enter Date (MM-DD-YYYY)', required: true }
      ]
    },
    errorMessage: 'Please provide a Date of Birth.'
  },
  {
    id: 'W8BEN.TAXTREATY.1',
    name: 'claimingTaxTreaty',
    info: helpText.claimingTaxTreaty,
    questionText: 'preQualifyingQuestionnaire.W8BEN.TAXTREATY.1.questionText',
    style: Style.Card,
    group: 'Tax Treaty',
    inputs: {
      type: InputType.YesNo,
      required: true,
      buttonTitles: [
        'Yes', 'No'
      ]
    },
    errorMessage: 'Please indicate if a reduced rate of withholding is being claimed under a tax treaty.'
  },
  {
    id: 'W8BEN.TAXTREATY.2',
    name: 'treatyCountry',
    info: helpText.taxTreatyCountry,
    questionText: 'preQualifyingQuestionnaire.W8BEN.TAXTREATY.2.questionText',
    style: Style.Embedded,
    group: 'Tax Treaty',
    dynamicUpdate: {
      inputQuestionId: 'W8BEN.TAXTREATY.1',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 0,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.CountryDropdown,
      defaultValue: { value: '0', label: 'Please Select Country' },
    }
  },
  {
    id: 'W8BEN.TAXTREATY.3',
    name: 'claimingSpecialRates',
    info: helpText.claimingSpecialRates,
    questionText: 'preQualifyingQuestionnaire.W8BEN.TAXTREATY.3.questionText',
    style: Style.Embedded,
    group: 'Tax Treaty',
    answer: 1,
    isAnswered: true,
    inputs: {
      type: InputType.YesNo,
      buttonTitles: [
        'Yes', 'No'
      ]
    }
  },
  {
    id: 'W8BEN.TAXTREATY.4',
    name: 'whdRate',
    info: helpText.treatyClaimWithholdingRate,
    questionText: 'preQualifyingQuestionnaire.W8BEN.TAXTREATY.4.questionText',
    style: Style.Embedded,
    group: 'Tax Treaty',
    dynamicUpdate: {
      inputQuestionId: 'W8BEN.TAXTREATY.3',
      condition: DynamicUpdateCondition.Is,
      expectedValue: 1,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.NumericField,
      maxValue: 100,
      fields: [
        { placeholder: 'Enter withholding rate', required: true }
      ]
    },
    errorMessage: 'Please provide the withholding rate being claimed.'
  },
  {
    id: 'W8BEN.TAXTREATY.5',
    name: 'incomeType',
    info: helpText.treatyClaimIncomeType,
    questionText: 'preQualifyingQuestionnaire.W8BEN.TAXTREATY.5.questionText',
    style: Style.Embedded,
    group: 'Tax Treaty',
    dynamicUpdate: {
      inputQuestionId: 'W8BEN.TAXTREATY.3',
      condition: DynamicUpdateCondition.Is,
      expectedValue: 1,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Enter income type', required: true }
      ]
    },
    errorMessage: 'Please provide the income type which applies to the withholding rate being claimed.'
  },
  {
    id: 'W8BEN.TAXTREATY.6',
    name: 'articleAndParagraph',
    info: helpText.treatyClaimArticleAndParagraph,
    questionText: 'preQualifyingQuestionnaire.W8BEN.TAXTREATY.6.questionText',
    style: Style.Embedded,
    group: 'Tax Treaty',
    dynamicUpdate: {
      inputQuestionId: 'W8BEN.TAXTREATY.3',
      condition: DynamicUpdateCondition.Is,
      expectedValue: 1,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Enter article and paragraph of tax treaty', required: true }
      ]
    },
    errorMessage: 'Please provide the relevant article and paragraph of the tax treaty.'
  },
  {
    id: 'W8BEN.TAXTREATY.7',
    name: 'explanation',
    info: helpText.treatyClaimExplanation,
    questionText: 'preQualifyingQuestionnaire.W8BEN.TAXTREATY.7.questionText',
    style: Style.Embedded,
    group: 'Tax Treaty',
    dynamicUpdate: {
      inputQuestionId: 'W8BEN.TAXTREATY.3',
      condition: DynamicUpdateCondition.Is,
      expectedValue: 1,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Enter an explanation why special rates and conditions apply', required: true }
      ]
    },
    errorMessage: 'Please provide an explanation why special rates and conditions apply.'
  },
  {
    id: 'W8BEN.Signer.1',
    name: 'signername',
    info: helpText.signerName,
    questionText: 'preQualifyingQuestionnaire.W8BEN.Signer.1.questionText',
    style: Style.Card,
    group: 'Signer Details',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Signer Name', required: true }
      ]
    },
    errorMessage: 'Signer name is required.'
  },
  {
    id: 'W8BEN.Signer.2',
    name: 'capacityField',
    info: helpText.capacity,
    questionText: 'preQualifyingQuestionnaire.W8BEN.Signer.2.questionText',
    style: Style.Embedded,
    group: 'Signer Details',
    complexCapacityContraint: {
      signerNameId: 'W8BEN.Signer.1',
      givenNameId: ['W8BEN.NAME.1'],
      expectedValue: 'Signing on Own Behalf'
    },
    inputs: {
      type: InputType.Dropdown,
      defaultValue: { value: 'Signing on Own Behalf', label: 'Signing on Own Behalf' },
      items: [
        { value: 'Signing on Own Behalf', label: 'Signing on Own Behalf' },
        { value: 'Conservator', label: 'Conservator' },
        { value: 'Custodian for Minor Child', label: 'Custodian for Minor Child' },
        { value: 'Father', label: 'Father' },
        { value: 'Guardian', label: 'Guardian' },
        { value: 'Mother', label: 'Mother' },
        { value: 'Power of Attorney', label: 'Power of Attorney' },
        { value: 'Attorney in Fact', label: 'Attorney in Fact' },
        { value: 'Authorized Signer', label: 'Authorized Signer' },
        { value: 'Other', label: 'Other' },
      ]
    },
    errorMessage: 'Capacity is required when name and signer name do not match.'
  },
  {
    id: 'W8BEN.Signer.3',
    name: 'capacityFieldOther',
    questionText: 'preQualifyingQuestionnaire.W8BEN.Signer.3.questionText',
    style: Style.Embedded,
    group: 'Signer Details',
    dynamicUpdate: {
      inputQuestionId: 'W8BEN.Signer.2',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 'Other',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Capacity', required: true }
      ]
    },
    errorMessage: 'Please provide the capacity in which you are acting.'
  }
];

export default w8benQuestions;
